import type { AxiosResponse } from 'axios';

import i18n from '../utils/i18n';

/**
 * Keys under the `toasts` namespace of src/locales/*.json that this mapper can
 * resolve. Every one of them must exist in all six locale files — the parity
 * test in src/locales/locales.test.ts imports this array to enforce that.
 *
 * Not every keyed message reaches the user: the mapper only writes `message`
 * into the Success payload, and toastSaga renders it solely for actions listed
 * in src/redux/toast/watchList.ts. `documentsUploaded` and `documentDeleted`
 * are stored but not currently watched, so they stay silent until their
 * actions join that list.
 */
export const SUCCESS_TOAST_KEYS = [
	'created',
	'updated',
	'deleted',
	'deletedMany',
	'approved',
	'documentsUploaded',
	'documentDeleted',
] as const;

export type SuccessToastKey = (typeof SUCCESS_TOAST_KEYS)[number];

const mapSuccessResponseToState = (
	response: AxiosResponse,
	toastKey?: SuccessToastKey,
) => ({
	data: response.data,
	rowCount: response.headers['x-total-count'] || 0,
	aggregate: response.headers['aggregated-values'] || null,
	// Resolved here, not at module scope: the user can switch language at any
	// time and the toast must speak whatever is selected when it is dispatched.
	// Read-only flows pass no key and get no `message` at all — their actions
	// are not watched, and toastSaga falls back to the translated
	// global.operationSucceeded anyway.
	...(toastKey && { message: i18n.t(`toasts.${toastKey}`) }),
	type: 'success',
});

export { mapSuccessResponseToState };
